import { formatCredits, type AiCreditBalance } from '@/lib/api/core/ai-credits-api'
import { featureLabel } from '@/lib/ai/ai-usage-labels'

const dateFormat = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })

function formatDate(iso: string): string {
    return dateFormat.format(new Date(iso))
}

interface Props {
    balance: AiCreditBalance
    /** Mostra o gasto do mês por funcionalidade (aba Assinatura). */
    showUsage?: boolean
}

/**
 * Saldo de créditos de IA: o total e de onde vem. O crédito do plano zera na
 * virada do mês; o comprado vale 12 meses e é gasto depois do do plano.
 */
export function AiCreditBalanceSummary({ balance, showUsage = false }: Props) {
    const unlimited = balance.planCredits === 'unlimited'
    const empty = !unlimited && balance.totalCredits <= 0

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className={`text-3xl font-bold tabular-nums ${empty ? 'text-error' : 'text-base-content'}`}>
                    {unlimited ? 'Ilimitado' : formatCredits(balance.totalCredits)}
                </span>
                {!unlimited && <span className="text-sm text-base-content/60">créditos disponíveis</span>}
            </div>

            {!unlimited && (balance.plan || balance.purchased.length > 0) && (
                <ul className="text-sm space-y-1.5">
                    {balance.plan && (
                        <li className="flex flex-wrap justify-between gap-x-4">
                            <span className="text-base-content/70">
                                Do plano: {formatCredits(balance.plan.remainingCredits)} de{' '}
                                {formatCredits(balance.plan.grantedCredits)}
                            </span>
                            <span className="text-base-content/50">
                                até {formatDate(balance.plan.expiresAt)}
                            </span>
                        </li>
                    )}
                    {balance.purchased.map((lot, index) => (
                        <li key={index} className="flex flex-wrap justify-between gap-x-4">
                            <span className="text-base-content/70">
                                Comprados: {formatCredits(lot.remainingCredits)} de{' '}
                                {formatCredits(lot.grantedCredits)}
                            </span>
                            <span className="text-base-content/50">válidos até {formatDate(lot.expiresAt)}</span>
                        </li>
                    ))}
                </ul>
            )}

            {empty && (
                <p className="text-sm text-error">
                    Sem créditos, a IA fica parada e o atendente de WhatsApp passa as conversas para a
                    equipe.
                </p>
            )}

            {showUsage && balance.monthUsage.length > 0 && (
                <div>
                    <p className="text-sm font-medium mb-2">Gasto neste mês</p>
                    <ul className="text-sm space-y-1">
                        {balance.monthUsage.map(row => (
                            <li key={row.feature} className="flex justify-between gap-4">
                                <span className="text-base-content/70">{featureLabel(row.feature)}</span>
                                <span className="tabular-nums">{formatCredits(row.credits)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
